import { api } from './api';
import { TeamMember, Invitation, CreateInvitationData } from '../store/teamStore';
import type { ApiResponse } from '../utils/types';
import { toast } from 'sonner';

const API_ENDPOINTS = {
  members: '/team/members',
  invitations: '/team/invitations',
  invitation: (id: string) => `/team/invitations/${id}`,
  member: (id: string) => `/team/members/${id}`,
  role: (id: string) => `/team/members/${id}/role`,
  rolePermissions: '/team/roles/permissions',
  updateRolePermissions: (role: string) => `/team/roles/${role}/permissions`,
};

function isErrorWithResponse(error: unknown): error is { response?: { status?: number; data?: { message?: string } } } {
  return typeof error === 'object' && error !== null && 'response' in error;
}

export class TeamService {
  // Get all team members
  async getMembers(): Promise<TeamMember[]> {
    try {
      const response = await api.get<ApiResponse<TeamMember[]>>(API_ENDPOINTS.members);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch team members');
      return response.data.data.map(this.transformMember);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
      throw new Error('Failed to fetch team members. Please try again.');
    }
  }

  // Get all invitations
  async getInvitations(): Promise<Invitation[]> {
    try {
      const response = await api.get<ApiResponse<Invitation[]>>(API_ENDPOINTS.invitations);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch invitations');
      return response.data.data.map(this.transformInvitation);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
      throw new Error('Failed to fetch invitations. Please try again.');
    }
  }

  // Invite new member
  async inviteMember(data: CreateInvitationData): Promise<Invitation> {
    try {
      const response = await api.post<ApiResponse<Invitation>>(API_ENDPOINTS.invitations, data);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to send invitation');

      toast.success('Invitation sent successfully');
      return this.transformInvitation(response.data.data);
    } catch (error) {
      console.error('Failed to send invitation:', error);
      const message = isErrorWithResponse(error) && error.response?.data?.message
        ? error.response.data.message
        : 'Failed to send invitation. Please try again.';
      toast.error(`${message}`);
      throw new Error(message);
    }
  }

  // Update member role
  async updateMemberRole(id: string, role: TeamMember['role']): Promise<TeamMember> {
    try {
      const response = await api.patch<ApiResponse<TeamMember>>(API_ENDPOINTS.role(id), { role });
      if (!response.data.success) throw new Error(response.data.message || 'Failed to update member role');

      toast.success('Member role updated successfully');
      return this.transformMember(response.data.data);
    } catch (error) {
      console.error('Failed to update member role:', error);
      let message = 'Failed to update member role. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Member not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(`${message}`);
      throw new Error(message);
    }
  }

  // Delete member
  async deleteMember(id: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.member(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete member');

      toast.success('Member removed successfully');
    } catch (error) {
      console.error('Failed to delete member:', error);
      let message = 'Failed to delete member. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Member not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(`${message}`);
      throw new Error(message);
    }
  }

  // Resend invitation
  async resendInvitation(id: string): Promise<void> {
    try {
      const response = await api.post<ApiResponse<void>>(`${API_ENDPOINTS.invitation(id)}/resend`);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to resend invitation');

      toast.success('Invitation resent successfully');
    } catch (error) {
      console.error('Failed to resend invitation:', error);
      let message = 'Failed to resend invitation. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Invitation not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(`${message}`);
      throw new Error(message);
    }
  }

  // Delete invitation
  async deleteInvitation(id: string): Promise<void> {
    try {
      const response = await api.delete<ApiResponse<void>>(API_ENDPOINTS.invitation(id));
      if (!response.data.success) throw new Error(response.data.message || 'Failed to delete invitation');

      toast.success('Invitation deleted successfully');
    } catch (error) {
      console.error('Failed to delete invitation:', error);
      let message = 'Failed to delete invitation. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.status === 404) message = 'Invitation not found';
        else if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(`${message}`);
      throw new Error(message);
    }
  }

  async acceptInvitation(invitationId: string): Promise<TeamMember> {
    try {
      const response = await api.post<ApiResponse<TeamMember>>(
        `${API_ENDPOINTS.invitation(invitationId)}/accept`
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to accept invitation');
      }

      toast.success('Invitation accepted successfully!');
      // Return the new team member
      return this.transformMember(response.data.data);
    } catch (error) {
      console.error('Failed to accept invitation:', error);
      const message =
        (error as any)?.response?.data?.message || 'Failed to accept invitation. Please try again.';
      toast.error(message);
      throw new Error(message);
    }
  }

  // Get role permissions
  async getRolePermissions(): Promise<Record<string, string[]>> {
    try {
      const response = await api.get<ApiResponse<Record<string, string[]>>>(API_ENDPOINTS.rolePermissions);
      if (!response.data.success) throw new Error(response.data.message || 'Failed to fetch role permissions');
      return response.data.data;
    } catch (error) {
      console.error('Failed to fetch role permissions:', error);
      throw new Error('Failed to fetch role permissions. Please try again.');
    }
  }

  // Update role permissions
  async updateRolePermissions(role: string, permissions: string[]): Promise<void> {
    try {
      const response = await api.put<ApiResponse<void>>(API_ENDPOINTS.updateRolePermissions(role), { permissions });
      if (!response.data.success) throw new Error(response.data.message || 'Failed to update role permissions');

      // toast.success('Role permissions updated successfully');
    } catch (error) {
      console.error('Failed to update role permissions:', error);
      let message = 'Failed to update role permissions. Please try again.';
      if (isErrorWithResponse(error)) {
        if (error.response?.data?.message) message = error.response.data.message;
      }
      toast.error(`${message}`);
      throw new Error(message);
    }
  }

  // Helper: normalize member data
  private transformMember(member: any): TeamMember {
    // Handle populated userId - if userId is an object, extract user info
    const userId = member.userId?._id || member.userId?.id || member.userId || member.id || member._id;
    const userName = member.userId?.name || member.name || '';
    const userEmail = member.userId?.email || member.email || '';
    const userAvatar = member.userId?.avatar || member.avatar;

    return {
      id: userId.toString(), // Use userId for task assignment
      name: userName,
      email: userEmail,
      role: member.role || 'Viewer',
      status: member.status || 'Offline',
      joinedAt: member.joinedAt || member.createdAt || new Date().toISOString(),
      lastActive: member.lastActive || member.updatedAt,
      isOwner: member.isOwner || false,
      avatar: userAvatar,
    };
  }

  // Helper: normalize invitation data
  private transformInvitation(invitation: any): Invitation {
    return {
      id: invitation.id || invitation._id,
      email: invitation.email || '',
      role: invitation.role || 'Viewer',
      invitedBy: invitation.invitedBy || '',
      message: invitation.message,
      status: invitation.status || 'pending',
      createdAt: invitation.createdAt || new Date().toISOString(),
      expiresAt: invitation.expiresAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
    };
  }
}

// Singleton instance
export const teamService = new TeamService();